import { pantryColors, pantryTypography } from '@pantry/ui'
import { useFormikContext } from 'formik'
import React from 'react'
import type { LoyaltyRules } from '../../../../../types/loyalty'
import { useRuleCardActions } from './RuleCardActionsContext'
import { RuleCardEditContext } from './RuleCardEditContext'
import { RuleToggle } from './RuleToggle'

const p = pantryColors
const F = pantryTypography.fontFamily

type CardPath = 'earn' | 'redemption' | 'bonus_points'

interface EditableRuleCardProps {
  title: string
  description: string
  icon: React.ReactNode
  // Sub-document this card edits (used to snapshot/revert on Cancel).
  cardPath: CardPath
  // Dotted Formik path of the card's master enable toggle. Omitted when the card
  // has no enable toggle (see `showEnableToggle`).
  enablePath?: string
  // Whether to render the header master enable/disable toggle. When false, the
  // card is always enabled (no on/off concept) and is never greyed out.
  showEnableToggle?: boolean
  children: React.ReactNode
}

const headerBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '5px 11px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  fontFamily: F,
  cursor: 'pointer',
}

export const EditableRuleCard: React.FC<EditableRuleCardProps> = ({
  title,
  description,
  icon,
  cardPath,
  enablePath,
  showEnableToggle = true,
  children,
}) => {
  const { values, setFieldValue } = useFormikContext<LoyaltyRules>()
  const { saveCard, isSaving } = useRuleCardActions()
  const [isEditing, setIsEditing] = React.useState(false)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const snapshotRef = React.useRef<LoyaltyRules[CardPath] | null>(null)

  const cardEnabled = showEnableToggle
    ? Boolean((values[cardPath] as { enable?: boolean })?.enable)
    : true

  const handleEdit = () => {
    snapshotRef.current = values[cardPath]
    setSaveError(null)
    setIsEditing(true)
  }

  const handleSave = async () => {
    // Persist only this card's changes (other open cards keep their unsaved
    // edits), then lock back to display mode if the save succeeded.
    const result = await saveCard(cardPath)
    if (result.ok) {
      setSaveError(null)
      snapshotRef.current = null
      setIsEditing(false)
    } else {
      setSaveError(
        result.error ?? "Couldn't save your changes. Please try again.",
      )
    }
  }

  const handleCancel = () => {
    if (snapshotRef.current !== null) {
      setFieldValue(cardPath, snapshotRef.current)
    }
    snapshotRef.current = null
    setSaveError(null)
    setIsEditing(false)
  }

  const handleToggleEnable = async (next: boolean) => {
    if (!enablePath) return
    // No footer to persist toggles, so save immediately. Pass the override since
    // Formik state updates asynchronously and would be stale on this tick.
    setFieldValue(enablePath, next)
    const result = await saveCard(cardPath, next)
    if (result.ok) {
      setSaveError(null)
    } else {
      // Revert the optimistic toggle so the switch reflects the persisted state.
      setFieldValue(enablePath, !next)
      setSaveError(
        result.error ?? "Couldn't save your changes. Please try again.",
      )
    }
  }

  return (
    <div
      style={{
        background: p.white,
        border: `1px solid ${p.gray[200]}`,
        borderRadius: 10,
        overflow: 'hidden',
        fontFamily: F,
      }}
    >
      <div style={{ padding: 20 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            paddingBottom: 14,
            borderBottom: `1px solid ${p.gray[100]}`,
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: cardEnabled ? p.gray[800] : p.gray[500],
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span
                style={{
                  color: cardEnabled ? p.brand.primary : p.gray[400],
                  display: 'flex',
                }}
              >
                {icon}
              </span>
              {title}
            </div>
            <div
              style={{
                fontSize: 12,
                color: cardEnabled ? p.gray[500] : p.gray[400],
                lineHeight: 1.45,
              }}
            >
              {description}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
            }}
          >
            {cardEnabled &&
              (isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={isSaving}
                    style={{
                      ...headerBtn,
                      border: `1px solid ${p.gray[200]}`,
                      color: p.gray[500],
                      background: p.white,
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                      ...headerBtn,
                      border: `1px solid ${p.brand.primary}`,
                      color: p.white,
                      background: p.brand.primary,
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.7 : 1,
                    }}
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleEdit}
                  style={{
                    ...headerBtn,
                    border: `1px solid ${p.gray[200]}`,
                    color: p.gray[600],
                    background: p.white,
                  }}
                >
                  Edit
                </button>
              ))}
            {showEnableToggle && (
              <RuleToggle
                checked={cardEnabled}
                onChange={handleToggleEnable}
                forceEnabled
              />
            )}
          </div>
        </div>
        {saveError && (
          <div
            role="alert"
            style={{
              marginTop: 12,
              padding: '8px 12px',
              borderRadius: 6,
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              color: '#B91C1C',
              fontSize: 12,
              lineHeight: 1.45,
            }}
          >
            {saveError}
          </div>
        )}
        <RuleCardEditContext.Provider value={{ isEditing, cardEnabled }}>
          <div
            style={{
              marginTop: 6,
              opacity: cardEnabled ? 1 : 0.5,
              pointerEvents: cardEnabled ? 'auto' : 'none',
              filter: cardEnabled ? 'none' : 'grayscale(0.4)',
            }}
          >
            {children}
          </div>
        </RuleCardEditContext.Provider>
      </div>
    </div>
  )
}
